import React, { Suspense } from 'react';
import { Route, Navigate } from 'react-router-dom';
import ProtectedRouteNew from '@/components/ProtectedRouteNew';
import ProtectedRoute from '@/components/ProtectedRoute';
import OnboardingGate from '@/components/OnboardingGate';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import AdminLayout from '@/components/AdminLayout';
import DashboardAccessGuard from '@/routes/DashboardAccessGuard';

// Lazy load main dashboard components
const AdminDashboard = React.lazy(() => import('@/pages/AdminDashboard'));
const EmployerDashboard = React.lazy(() => import('@/pages/EmployerDashboard'));
const JobSeekerDashboard = React.lazy(() => import('@/pages/JobSeekerDashboard'));
const AdminOverviewPage = React.lazy(() => import('@/pages/admin/AdminOverviewPage'));
const AdminRpcHealthPage = React.lazy(() => import('@/pages/admin/AdminRpcHealthPage'));
const AdminMatchingLabPage = React.lazy(() => import('@/pages/admin/AdminMatchingLabPage'));
const AdminFaqPage = React.lazy(() => import('@/pages/admin/AdminFaqPage'));
const AffiliatesAdminPage = React.lazy(() => import('@/pages/admin/AffiliatesAdminPage'));
const AdminGuidePage = React.lazy(() => import('@/pages/admin/AdminGuidePage'));
const AdminBillingPage = React.lazy(() => import('@/pages/admin/AdminBillingPage'));
const AdminBlogListPage = React.lazy(() => import('@/pages/admin/BlogListPage'));
const AdminBlogEditorPage = React.lazy(() => import('@/pages/admin/BlogEditorPage'));
const AdminVerification = React.lazy(() => import('@/pages/AdminVerification'));
const PendingApproval = React.lazy(() => import('@/pages/PendingApproval'));
const EmployerOnboarding = React.lazy(() => import('@/pages/EmployerOnboarding'));
const JobSeekerOnboarding = React.lazy(() => import('@/pages/JobSeekerOnboarding'));
const EmployerNewPosition = React.lazy(() => import('@/pages/EmployerNewPosition'));
const JobApplication = React.lazy(() => import('@/pages/JobApplication'));
const EmployerAwaitingApproval = React.lazy(() => import('@/pages/EmployerAwaitingApproval'));
const JobSeekerAwaitingApproval = React.lazy(() => import('@/pages/JobSeekerAwaitingApproval'));

export const LazyRoutes = () => {
  return [
    /* ADMIN ROUTES - Using AdminLayout */
    <Route key="admin" element={<ProtectedRouteNew requireRole="admin" />}>
      <Route path="admin" element={<AdminLayout />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={
          <Suspense fallback={<LoadingSkeleton height={520} />}>
            <AdminOverviewPage />
          </Suspense>
        } />
        <Route path="rpc-health" element={
          <Suspense fallback={<LoadingSkeleton height={520} />}>
            <AdminRpcHealthPage />
          </Suspense>
        } />
        <Route path="matching-lab" element={
          <Suspense fallback={<LoadingSkeleton height={520} />}>
            <AdminMatchingLabPage />
          </Suspense>
        } />
        <Route path="billing" element={
          <Suspense fallback={<LoadingSkeleton height={520} />}>
            <AdminBillingPage />
          </Suspense>
        } />
        <Route path="faq" element={
          <Suspense fallback={<LoadingSkeleton height={520} />}>
            <AdminFaqPage />
          </Suspense>
        } />
        <Route path="affiliates" element={
          <Suspense fallback={<LoadingSkeleton height={520} />}>
            <AffiliatesAdminPage />
          </Suspense>
        } />
        <Route path="guide" element={
          <Suspense fallback={<LoadingSkeleton height={520} />}>
            <AdminGuidePage />
          </Suspense>
        } />
        <Route path="blogs" element={
          <Suspense fallback={<LoadingSkeleton height={520} />}>
            <AdminBlogListPage />
          </Suspense>
        } />
        <Route path="blogs/:postId" element={
          <Suspense fallback={<LoadingSkeleton height={520} />}>
            <AdminBlogEditorPage />
          </Suspense>
        } />
        <Route path="verification" element={
          <Suspense fallback={<LoadingSkeleton height={520} />}>
            <AdminVerification />
          </Suspense>
        } />
        <Route path="pending-approval" element={
          <Suspense fallback={<LoadingSkeleton height={520} />}>
            <PendingApproval />
          </Suspense>
        } />
      </Route>
    </Route>,
    
    /* EMPLOYER ROUTES */
    <Route key="employer" element={<ProtectedRouteNew requireRole="employer" />}>
      <Route path="employer/onboarding" element={
        <Suspense fallback={<LoadingSkeleton height={520} />}>
          <EmployerOnboarding />
        </Suspense>
      } />
      <Route path="employer/awaiting-approval" element={
        <Suspense fallback={<LoadingSkeleton height={520} />}>
          <EmployerAwaitingApproval />
        </Suspense>
      } />
      <Route path="employer/dashboard" element={
        <OnboardingGate>
          <Suspense fallback={<LoadingSkeleton height={520} />}>
            <EmployerDashboard />
          </Suspense>
        </OnboardingGate>
      } />
      <Route path="employer/new-position" element={
        <OnboardingGate>
          <Suspense fallback={<LoadingSkeleton height={520} />}>
            <EmployerNewPosition />
          </Suspense>
        </OnboardingGate>
      } />
    </Route>,
    
    /* JOB SEEKER ROUTES */
    <Route key="job-seeker" element={<ProtectedRouteNew requireRole="job_seeker" />}>
      <Route path="job-seeker/dashboard" element={
        <OnboardingGate>
          <Suspense fallback={<LoadingSkeleton height={520} />}>
            <JobSeekerDashboard />
          </Suspense>
        </OnboardingGate>
      } />
      <Route path="job-seeker/awaiting-approval" element={
        <Suspense fallback={<LoadingSkeleton height={520} />}>
          <JobSeekerAwaitingApproval />
        </Suspense>
      } />
    </Route>,
    
    /* ONBOARDING ROUTES */
    <Route key="job-seeker-onboarding" path="job-seeker/onboarding" element={
      <Suspense fallback={<LoadingSkeleton height={520} />}>
        <JobSeekerOnboarding />
      </Suspense>
    } />,
    
    /* APPLICATION ROUTE */
    <Route key="job-application" path="job-application" element={
      <Suspense fallback={<LoadingSkeleton height={520} />}>
        <JobApplication />
      </Suspense>
    } />,

    /* GENERIC PENDING APPROVAL (for backward compatibility) */
    <Route key="pending-approval" path="pending-approval" element={
      <ProtectedRouteNew>
        <Suspense fallback={<LoadingSkeleton height={520} />}>
          <PendingApproval />
        </Suspense>
      </ProtectedRouteNew>
    } />
  ];
};
